import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Building2, CreditCard, Shield, Wallet as WalletIcon } from "lucide-react";
import { createCharge, createPaymentIntent } from "../services/paymentService";
import { payWithCredits, formatCreditsAsRand } from "../services/walletService";
import { useWallet } from "../context/WalletContext";
import TopUpDialog, { MIN_TOPUP } from "./TopUpDialog";

const PaymentCheckout = ({
  amount,
  bookingDetails,
  onPaymentSuccess,
  onPaymentCancel,
}) => {
  const { balance: creditBalance, hasWallet, setBalance } = useWallet();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [pendingChargeId, setPendingChargeId] = useState(null);
  const redirectTimeoutRef = useRef(null);

  // Amount is passed in cents; credits are denominated in rands (1 credit = R1).
  const amountInCredits = amount / 100;
  const hasEnoughCredits = creditBalance >= amountInCredits;
  const shortfall = Math.max(amountInCredits - creditBalance, 0);
  // Stitch won't collect less than the minimum top-up, so a small shortfall
  // means loading a bit more than strictly needed — the rest stays banked.
  const topUpAmount = Math.max(Math.ceil(shortfall), MIN_TOPUP);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const buildChargeData = () => ({
    // The API field is `service_booking`; sending `booking` silently drops the link.
    service_booking: bookingDetails?.id,
    amount: (amount / 100).toFixed(2), // Convert cents to ZAR with 2 decimals
    description:
      bookingDetails?.description ||
      `Pet service - ${bookingDetails?.serviceType || "Booking"}`,
    charge_type: "service",
  });

  const handlePayWithCredits = async () => {
    try {
      setProcessing(true);
      setError(null);

      const charge = await createCharge(buildChargeData());

      if (!charge || !charge.id) {
        throw new Error("Failed to create payment charge. Please try again.");
      }

      const result = await payWithCredits(charge.id);

      setBalance(result.balance);
      setProcessing(false);

      if (onPaymentSuccess) {
        onPaymentSuccess({ ...result, charge_id: charge.id, method: "credits" });
      }
    } catch (err) {
      console.error("Credit payment error:", err);
      setError(
        err.insufficientCredits
          ? "You don't have enough credits for this booking. Top up your wallet or pay via Stitch."
          : err.message || "Failed to pay with credits. Please try again.",
      );
      setProcessing(false);
    }
  };

  /**
   * Top up and pay in one step.
   *
   * Creates the booking charge first, then hands its id to the top-up so the
   * backend pays it the moment the credits land. The user makes one payment;
   * the settlement happens server-side, so it completes even if they close
   * the tab on the Stitch page.
   */
  const handleTopUpAndPay = async () => {
    try {
      setProcessing(true);
      setError(null);

      const charge = await createCharge(buildChargeData());

      if (!charge || !charge.id) {
        throw new Error("Failed to create payment charge. Please try again.");
      }

      setPendingChargeId(charge.id);
      setProcessing(false);
      setTopUpOpen(true);
    } catch (err) {
      console.error("Top-up and pay error:", err);
      setError(err.message || "Failed to start payment. Please try again.");
      setProcessing(false);
    }
  };

  const handleProcessPayment = async () => {
    // Clear any existing timeout from previous attempts
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }

    try {
      setProcessing(true);
      setError(null);

      // Step 1: Create a charge
      const charge = await createCharge(buildChargeData());

      if (!charge || !charge.id) {
        throw new Error("Failed to create payment charge. Please try again.");
      }

      // Step 2: Create payment intent to get Stitch URL
      const paymentIntent = await createPaymentIntent(charge.id);

      if (!paymentIntent || !paymentIntent.payment_url) {
        throw new Error("Failed to get payment URL. Please try again.");
      }

      // Step 3: Store charge ID for verification later
      localStorage.setItem("pending_charge_id", charge.id.toString());

      // Step 4: Redirect to Stitch payment page
      const paymentUrl = paymentIntent.payment_url;
      window.location.href = paymentUrl;

      // Fallback: If still on page after 3 seconds, redirect may have failed
      redirectTimeoutRef.current = setTimeout(() => {
        redirectTimeoutRef.current = null;

        setProcessing((currentProcessing) => {
          if (currentProcessing) {
            setError(
              <Box>
                <Typography variant="body2" gutterBottom>
                  Redirect to payment page may be blocked. Please click below to
                  continue:
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => window.open(paymentUrl, "_blank")}
                  sx={{ mt: 1 }}
                >
                  Complete Payment
                </Button>
              </Box>,
            );
            return false;
          }
          return currentProcessing;
        });
      }, 3000);
    } catch (err) {
      console.error("Payment processing error:", err);
      const errorMessage =
        err.message || "Failed to initiate payment. Please try again.";
      setError(errorMessage);
      setProcessing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(value / 100);
  };

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Summary
          </Typography>

          {bookingDetails && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Service: {bookingDetails.serviceType || "Pet Service"}
              </Typography>
              {bookingDetails.petName && (
                <Typography variant="body2" color="text.secondary">
                  Pet: {bookingDetails.petName}
                </Typography>
              )}
              {bookingDetails.date && (
                <Typography variant="body2" color="text.secondary">
                  Date: {new Date(bookingDetails.date).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Total Amount</Typography>
            <Typography variant="h5" color="primary">
              {formatCurrency(amount)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Payment Method
          </Typography>

          {hasWallet && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                border: 1,
                borderColor: hasEnoughCredits ? "success.main" : "divider",
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <WalletIcon size={20} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Pay with credits
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Balance: {formatCreditsAsRand(creditBalance)}
                </Typography>
              </Box>

              {hasEnoughCredits ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formatCreditsAsRand(amountInCredits)} will be deducted from
                    your wallet. No redirect needed.
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handlePayWithCredits}
                    disabled={processing}
                    startIcon={
                      processing ? <CircularProgress size={20} /> : <WalletIcon size={18} />
                    }
                  >
                    Pay {formatCurrency(amount)} with credits
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    You're {formatCreditsAsRand(shortfall)} short. Load credits
                    and we'll pay for this booking with them straight away — one
                    payment, no second step.
                  </Typography>
                  {topUpAmount > shortfall && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      Minimum top-up is R{MIN_TOPUP}, so{" "}
                      {formatCreditsAsRand(topUpAmount - shortfall)} will stay in
                      your wallet for next time.
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={handleTopUpAndPay}
                    disabled={processing}
                    startIcon={
                      processing ? <CircularProgress size={20} /> : <WalletIcon size={18} />
                    }
                    sx={{ mt: topUpAmount > shortfall ? 0 : 2 }}
                  >
                    Add credits &amp; pay
                  </Button>
                </>
              )}
            </Box>
          )}

          {hasWallet && <Divider sx={{ mb: 3 }}>or</Divider>}

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Secure Payment via Stitch
            </Typography>
            <Typography variant="body2">
              You'll be redirected to complete your payment securely. Stitch
              supports instant EFT and card payments.
            </Typography>
          </Alert>

          <List sx={{ mb: 2 }}>
            <ListItem>
              <ListItemIcon>
                <Building2 size={24} color="#1976d2" />
              </ListItemIcon>
              <ListItemText
                primary="Instant EFT"
                secondary="Pay directly from your bank account"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CreditCard size={24} color="#1976d2" />
              </ListItemIcon>
              <ListItemText
                primary="Card Payment"
                secondary="Visa, Mastercard, and more"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Shield size={24} color="#1976d2" />
              </ListItemIcon>
              <ListItemText
                primary="Secure & Encrypted"
                secondary="Bank-grade security for all transactions"
              />
            </ListItem>
          </List>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={onPaymentCancel}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleProcessPayment}
              disabled={processing}
              startIcon={processing ? <CircularProgress size={20} /> : null}
            >
              {processing
                ? "Redirecting to Stitch..."
                : `Pay ${formatCurrency(amount)}`}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Loads credits and settles the booking charge with them in one payment */}
      <TopUpDialog
        open={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        defaultAmount={topUpAmount}
        settleChargeId={pendingChargeId}
        title="Add credits & pay"
        helperText={`You'll pay ${formatCurrency(
          amount,
        )} for this booking using the credits you load. Anything left over stays in your wallet.`}
      />
    </Box>
  );
};

export default PaymentCheckout;
